import { UserToken } from "../../types/token/token";
import { DeleteLanePayload, NewLaneDTO } from "../../types/kanban/lane";
import { SearchQueryRequest } from "../../types/searchQuery/searchQuery";
import { CollectionViewList,Collection,Document, MoveItemLaneRequest, ExternalFormPayload, ExternalFormUpdatePayload } from "../../models";
import { cacheKanbanOperations } from "../../utils";

interface GetKanbanResponse {
    data: Document[]
    count: number
}

const initSearchResponse: GetKanbanResponse = {
    data: [],
    count: 0
}

interface GetKanbanOptions {
    abortController?: AbortController | null
}

export default class GroupService {
    #token: UserToken = null
    #url: string = ''
    constructor(token: UserToken) {
        this.#token = token;
    }

    getHeaders() {
        return {
            'Authorization': 'Bearer ' + this.#token
        };
    }

    postHeaders() {
        return {
            'Authorization': 'Bearer ' + this.#token,
            'Content-Type': 'application/json'
        };
    }

    setToken = (token: UserToken, url?: string) => {
        this.#token = token;
        this.#url = url ?? "";
    }

    getToken() {
        return this.#token;
    }

    async getKanbanData(searchQuery: SearchQueryRequest, options: GetKanbanOptions): Promise<GetKanbanResponse> {
        try {
            const address = `${this.#url}/search/kanban-web`
            const res = await fetch(address, {
                method: 'POST',
                body: JSON.stringify(searchQuery),
                headers: this.postHeaders(),
                signal: options?.abortController?.signal
            })
            
            if(!res) return initSearchResponse
            const result: {id: string, document: Document}[] = await res.json()
            return {
                count: result?.length ?? 0,
                data: result?.map(i => i.document)
            }
        }
        catch(err) {
            //@ts-ignore
            if (err.name == 'AbortError') {
                return initSearchResponse
            }

            return initSearchResponse
        }
    }

    async addNewLane(lane: NewLaneDTO, options: GetKanbanOptions): Promise<{val: CollectionViewList | null, isAborted: boolean}> {
        try {
            const address = `${this.#url}/collections/newKanbanLane`
            const res = await fetch(address, {
                method: 'POST',
                body: JSON.stringify(lane),
                headers: this.postHeaders(),
                signal: options?.abortController?.signal
            })
            
            if(!res) return {isAborted: false, val: null}
            const result: CollectionViewList = await res.json()
            result.items = []
            result.rules = []
            return {
                val: result,
                isAborted: false
            }
        }
        catch(err) {
            //@ts-ignore
            if (err.name == 'AbortError') {
                return {val: null, isAborted: true}
            }

            return {isAborted: false, val: null}
        }
    }

    async groupMailingService(collectionId: number, enableEmail: boolean, options: GetKanbanOptions): Promise<{val: string, isAborted: boolean}> {
        try {
            const address = `${this.#url}/collections/setupmail?collectionId=${collectionId}&enableEmail=${enableEmail}`
            const res = await fetch(address, {
                method: 'GET',
                headers: this.getHeaders(),
                signal: options?.abortController?.signal
            })
            
            if(!res) return { val: '', isAborted: false }
            const res1: {result: string, status: boolean} = await res.json()
            
            return { val: res1.result, isAborted: false }
        }
        catch(err) {
            //@ts-ignore
            if (err.name == 'AbortError') {
                return { val: '', isAborted: true }
            }
            return { val: '', isAborted: false }
        }
    }

    async updateNewLane(lane: CollectionViewList, collectionId: number, options: GetKanbanOptions): Promise<{val: boolean, isAborted: boolean}> {
        try {
            const address = `${this.#url}/collections/updateNewLane?collId=${collectionId}`
            const res = await fetch(address, {
                method: 'PUT',
                body: JSON.stringify(lane),
                headers: this.postHeaders(),
                signal: options?.abortController?.signal
            })
            
            if(!res) return {isAborted: false, val: false}
            const result: {status: boolean} = await res.json()
            return {
                val: result.status,
                isAborted: false
            }
        }
        catch(err) {
            //@ts-ignore
            if (err.name == 'AbortError') {
                return {val: false, isAborted: true}
            }

            return {isAborted: false, val: false}
        }
    }

    async updateLane(lane: CollectionViewList, options: GetKanbanOptions): Promise<{val: boolean, isAborted: boolean}> {
        try {
            const address = `${this.#url}/collections/updateLane`
            const res = await fetch(address, {
                method: 'PUT',
                body: JSON.stringify(lane),
                headers: this.postHeaders(),
                signal: options?.abortController?.signal
            })
            return {
                val: res.status === 200,
                isAborted: false
            }
        }
        catch(err) {
            //@ts-ignore
            if (err.name == 'AbortError') {
                return {val: false, isAborted: true}
            }

            return {isAborted: false, val: false}
        }
    }

   

    async updateGroup(collection: Collection, options: GetKanbanOptions): Promise<{val: Collection | undefined, isAborted: boolean}> {
        try {
            const address = `${this.#url}/collections/update`

            const res = await fetch(address, {
                method: 'POST',
                headers: this.postHeaders(),
                body: JSON.stringify(collection),
                signal: options?.abortController?.signal
            })
            
            if(!res || !res.ok) return { val: undefined, isAborted: false }
            
            const res1 = await res.json()
            return { val: res1, isAborted: false }
        }
        catch(err) {
            //@ts-ignore
            if (err.name === 'AbortError') {
                return { val: undefined, isAborted: true }
            }
            
            return { val: undefined, isAborted: false }
        }
    }

    async markFavouriteCollection(collectionUid: string, isFavourite: boolean, options: GetKanbanOptions): Promise<{val: boolean, isAborted: boolean}> {
        try {
            const address = isFavourite ?
                `${this.#url}/collections/AddCollectionToFavorites?collectionUid=${collectionUid}` :
                `${this.#url}/collections/RemoveCollectionFromFavorites?collectionUid=${collectionUid}`

            const res = await fetch(address, {
                method: 'GET',
                headers: this.getHeaders(),
                signal: options?.abortController?.signal
            })
            
            if(!res || !res.ok) return { val: false, isAborted: false }
            
            const res1: boolean = await res.json()
            
            return { val: res1, isAborted: false }
        }
        catch(err) {
            //@ts-ignore
            if (err.name === 'AbortError') {
                return { val: false, isAborted: true }
            }
            
            return { val: false, isAborted: false }
        }
    }

    async fetchCollection(collectionUid: string, options: GetKanbanOptions): Promise<{val: Collection | undefined, isAborted: boolean}> {
        try {
            const address = `${this.#url}/collections/info?collectionUid=${collectionUid}`
            const res = await fetch(address, {
                method: 'GET',
                headers: this.getHeaders(),
                signal: options?.abortController?.signal
            })
            const responseBodyText = await res.text()
            const requestResult: Collection = responseBodyText.length ? JSON.parse(responseBodyText) : {}
            
            return {
                val: requestResult,
                isAborted: false
            }
        }
        catch(err) {            
            //@ts-ignore
            if (err.name == 'AbortError') {
                return {val: undefined, isAborted: true}
            }

            return {isAborted: false, val: undefined}
        }
    }

    async fetchLibrary(): Promise<{val: Collection[], isAborted: boolean}> {
        try {
            const address = `${this.#url}/library`
            const res = await fetch(address, {
                method: 'GET',
                headers: this.getHeaders(),
                signal: undefined
            })
            const responseBodyText = await res.text()
            const requestResult: Collection[] = responseBodyText.length ? JSON.parse(responseBodyText) : []
            
            return {
                val: requestResult,
                isAborted: false
            }
        }
        catch(err) {            
            //@ts-ignore
            if (err.name == 'AbortError') {
                return {val: [], isAborted: true}
            }

            return {isAborted: false, val: []}
        }
    }


    
    async deleteLane(collection: DeleteLanePayload, options: GetKanbanOptions): Promise<{val: boolean, isAborted: boolean}> {
        try {
            const address = `${this.#url}/collections/deleteLane/`
            const res = await fetch(address, {
                method: 'DELETE',
                body: JSON.stringify(collection),
                headers: this.postHeaders(),
                signal: options?.abortController?.signal
            })
            
            return {
                val: res.status === 200,
                isAborted: false
            }
        }
        catch(err) {
            //@ts-ignore
            if (err.name == 'AbortError') {
                return {val: false, isAborted: true}
            }

            return {isAborted: false, val: false}
        }
    }

       async moveLane(collection:any, options: GetKanbanOptions): Promise<{val: boolean, isAborted: boolean}> {
        try {
            const address = `${this.#url}/collections/moveLane/`
            const res = await fetch(address, {
                method: 'PUT',
                body: JSON.stringify(collection),
                headers: this.postHeaders(),
                signal: options?.abortController?.signal
            })
            
            return {
                val: res.status === 200,
                isAborted: false
            }
        }
        catch(err) {
            //@ts-ignore
            if (err.name == 'AbortError') {
                return {val: false, isAborted: true}
            }

            return {isAborted: false, val: false}
        }
    }
    async moveCard(movedCard: MoveItemLaneRequest) {
        try {
            cacheKanbanOperations.addMoveOp({actionType: 'MoveCard', data: {
                docId: movedCard.cardId,
                sourceLane: movedCard.sourceLaneId + '',
                targetLane: movedCard.targetLaneId + ''
            }})

            const address = `${this.#url}/collections/moveCard/`
            return fetch(address, {
                method: 'PUT',
                body: JSON.stringify(movedCard),
                headers: this.postHeaders()
            })
        }
        catch(err) {
            //@ts-ignore
            if (err.name == 'AbortError') {
                return {val: false, isAborted: true}
            }

            return {isAborted: false, val: false}
        }
    }

    async createExternalForm(form: ExternalFormPayload) {
        try {
            const address = `${this.#url}/collections/createExternalInput/`
            const res = await fetch(address, {
                method: 'POST',
                body: JSON.stringify(form),
                headers: this.postHeaders()
            })

            const response: {result: string, status: string} = await res.json()
            if(!response.result || response.status !== 'Ok') return ''
            return response.result
        }
        catch(err) {
            //@ts-ignore
            if (err.name == 'AbortError') {
                return ''
            }

            return ''
        }
    }

    async updateExternalFormDescription(id: number, body: ExternalFormUpdatePayload) {
        try {
            const address = `${this.#url}/externalFormSettings/description?externalInputId=${id}`
            const res = await fetch(address, {
                method: 'PUT',
                body: JSON.stringify(body),
                headers: this.postHeaders()
            })

            const response: {status: string} = await res.json()
            if(response.status !== 'Ok') return false
            return true
        }
        catch(err) {
            return false
        }
    }
    async updateExternalFormSubmitedMessage(id: number, body: ExternalFormUpdatePayload) {
        try {
            const address = `${this.#url}/externalFormSettings/finishMessage?externalInputId=${id}`
            const res = await fetch(address, {
                method: 'PUT',
                body: JSON.stringify(body),
                headers: this.postHeaders()
            })

            const response: {status: string} = await res.json()
            if(response.status !== 'Ok') return false
            return true
        }
        catch(err) {
            return false
        }
    }
    async updateExternalFormDataType(id: number, body: ExternalFormUpdatePayload) {
        try {
            const address = `${this.#url}/externalFormSettings/datatype?externalInputId=${id}`
            const res = await fetch(address, {
                method: 'PUT',
                body: JSON.stringify(body),
                headers: this.postHeaders()
            })

            const response: {status: string} = await res.json()
            if(response.status !== 'Ok') return false
            return true
        }
        catch(err) {
            return false
        }
    }
    async updateExternalFormFields(id: number, body: ExternalFormUpdatePayload) {
        try {
            const address = `${this.#url}/externalFormSettings/fields?externalInputId=${id}`
            const res = await fetch(address, {
                method: 'PUT',
                body: JSON.stringify(body),
                headers: this.postHeaders()
            })

            const response: {status: string} = await res.json()
            if(response.status !== 'Ok') return false
            return true
        }
        catch(err) {
            return false
        }
    }
    async updateExternalFormInactiveTitle(id: number, body: ExternalFormUpdatePayload) {
        try {
            const address = `${this.#url}/externalFormSettings/inactiveTitle?externalInputId=${id}`
            const res = await fetch(address, {
                method: 'PUT',
                body: JSON.stringify(body),
                headers: this.postHeaders()
            })

            const response: {status: string} = await res.json()
            if(response.status !== 'Ok') return false
            return true
        }
        catch(err) {
            return false
        }
    }
    async updateExternalFormInactiveMessage(id: number, body: ExternalFormUpdatePayload) {
        try {
            const address = `${this.#url}/externalFormSettings/inactiveMessage?externalInputId=${id}`
            const res = await fetch(address, {
                method: 'PUT',
                body: JSON.stringify(body),
                headers: this.postHeaders()
            })

            const response: {status: string} = await res.json()
            if(response.status !== 'Ok') return false
            return true
        }
        catch(err) {
            return false
        }
    }
    async deleteExternalForm(collectionId: number, externalInputId: number) {
        try {
            const address = `${this.#url}/collections/deleteExternalInput?collectionId=${collectionId}&inputId=${externalInputId}`
            const res = await fetch(address, {
                method: 'DELETE',
                headers: this.getHeaders()
            })

            const response: {status: string} = await res.json()
            if(response.status !== 'Ok') return false
            return true
        }
        catch(err) {
            return false
        }
    }
    async updateExternalFormStatus(id: number, body: ExternalFormUpdatePayload) {
        try {
            const address = `${this.#url}/externalFormSettings/state?externalInputId=${id}`
            const res = await fetch(address, {
                method: 'PUT',
                body: JSON.stringify(body),
                headers: this.postHeaders()
            })

            const response: {status: string} = await res.json()
            if(response.status !== 'Ok') return false
            return true
        }
        catch(err) {
            return false
        }
    }
    async updateExternalFormTitle(id: number, body: ExternalFormUpdatePayload) {
        try {
            const address = `${this.#url}/externalFormSettings/title?externalInputId=${id}`
            const res = await fetch(address, {
                method: 'PUT',
                body: JSON.stringify(body),
                headers: this.postHeaders()
            })

            const response: {status: string} = await res.json()
            if(response.status !== 'Ok') return false
            return true
        }
        catch(err) {
            return false
        }
    }
    async updateExternalFormLabels(id: number, body: ExternalFormUpdatePayload) {
        try {
            const address = `${this.#url}/externalFormSettings/labels?externalInputId=${id}`
            const res = await fetch(address, {
                method: 'PUT',
                body: JSON.stringify(body),
                headers: this.postHeaders()
            })

            const response: {status: string} = await res.json()
            if(response.status !== 'Ok') return false
            return true
        }
        catch(err) {
            return false
        }
    }
    async updateExternalFormImg(id: number, body: ExternalFormUpdatePayload) {
        try {
            const address = `${this.#url}/externalFormSettings/headerFile?externalInputId=${id}`
            const res = await fetch(address, {
                method: 'PUT',
                body: JSON.stringify(body),
                headers: this.postHeaders()
            })

            const response: {status: string} = await res.json()
            if(response.status !== 'Ok') return false
            return true
        }
        catch(err) {
            return false
        }
    }

    //     return fetch(this.backendUrl + '/collections/moveLane/', {
    //         method: 'PUT',
    //         body: JSON.stringify(collection),
    //         headers: this.postHeaders()
    //     }).then(res => res.json ? res.json() : res);
    // }
}

